import React, {useEffect, useState} from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Text, Spinner, Alert, AlertIcon
} from "@chakra-ui/react";
import {api} from "../../../api/timesheetAPI";
import {useNavigate} from "react-router-dom";
import {showSwal} from "../../../common_components/Alert";
import {useAppDispatch, useAppSelector} from "../../../stateManagement/hooks";
import {jiraBoardrefreshNofilters, setTimeTableLoading, timetableRefresh} from "../../../stateManagement/refreshers";
import {Week} from "../types";
import {DEFAULT_CATEGORY, FavoriteEntry, loadFavorites} from "./favorites";
import {absenceBlockMessage, isAbsenceCodeBlocked} from "./absenceRestriction";
import {buildEditActions, buildEditRows, extractHoursByJiraAndDate} from "./rowMapping";
import {buildWorkingRows, EditableWeekTable, rowsToResult, WorkingRow} from "./EditableWeekTable";
import {ProgressOverlay} from "./ProgressOverlay";
import {saveWorklogEntry, updateBookingHours} from "./weekActionsApi";
import {InfoBar} from "./WeekActionsUI";

interface EditWeekDialogProps {
    isOpen: boolean;
    onClose: () => void;
    targetAnchor: string; // Monday of the currently displayed week (ISO date)
}

type Phase = "loading" | "review" | "progress";

export function EditWeekDialog({isOpen, onClose, targetAnchor}: EditWeekDialogProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const impersonate = useAppSelector((s) => s.impersonation.impersonatedUser);
    const currentUserName = useAppSelector((s) => s.user.user.name);

    const [phase, setPhase] = useState<Phase>("loading");
    const [rows, setRows] = useState<WorkingRow[]>([]);
    const [targetWeek, setTargetWeek] = useState<Week | null>(null);
    const [targetDates, setTargetDates] = useState<string[]>([]);
    const [originalMap, setOriginalMap] = useState<Map<string, number>>(new Map());
    const [jiraBaseUrl, setJiraBaseUrl] = useState("");
    const [favorites, setFavorites] = useState<FavoriteEntry[]>([]);
    const [progress, setProgress] = useState({done: 0, total: 0, message: ""});
    const [validationError, setValidationError] = useState("");

    useEffect(() => {
        if (!isOpen) { return; }
        let cancelled = false;
        setPhase("loading");
        setValidationError("");
        setFavorites(loadFavorites(currentUserName));

        api.getTimeTable(targetAnchor, "weekly", impersonate).then((res) => {
            if (cancelled) { return; }
            const week: Week = res.data.weeks[0];
            const editRows = buildEditRows(week, (jiraId) => isAbsenceCodeBlocked(jiraId, currentUserName));
            if (editRows.length === 0) {
                showSwal("failed", "Selected week has no booked entries to edit.");
                onClose();
                return;
            }
            const blocked = editRows.filter((r) => r.blocked);
            if (blocked.length > 0) {
                const codes = Array.from(new Set(blocked.map((r) => r.jiraId))).join(", ");
                showSwal("failed", `${absenceBlockMessage(currentUserName)}\n\nAffected code(s): ${codes}\n\nThese rows are read-only. Delete + Apply to remove them.`);
            }
            setTargetWeek(week);
            setTargetDates(week.week_dates);
            setOriginalMap(extractHoursByJiraAndDate(week));
            setJiraBaseUrl(res.data.JIRA_BASE_URL || "");
            setRows(buildWorkingRows(editRows));
            setPhase("review");
        }).catch((err) => {
            if (cancelled) { return; }
            showSwal("failed", err?.response?.data ?? String(err));
            onClose();
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, targetAnchor]);

    async function handleApply() {
        if (!targetWeek) { return; }
        const result = rowsToResult(rows, "edit");
        if (result === null) {
            setValidationError("Hours must be empty or numbers between 0 and 24.");
            return;
        }
        setValidationError("");
        const {increaseActions, reductionActions, conflicts, skippedApprovedChanges} = buildEditActions(result, targetWeek, targetDates, originalMap);

        if (conflicts.length > 0) {
            const lines = conflicts.slice(0, 12).map((c) => `\u2022 ${c.jiraId} on ${c.day} (${c.date})${c.comment ? ` \u2014 "${c.comment}"` : ""}`);
            showSwal("failed", `${conflicts.length} entr${conflicts.length === 1 ? "y" : "ies"} conflict with an existing not-approved booking:\n\n${lines.join("\n")}`);
            return;
        }

        if (increaseActions.length === 0 && reductionActions.length === 0) {
            showSwal("failed", skippedApprovedChanges > 0
                ? `${skippedApprovedChanges} approved cell change(s) were skipped.`
                : "No changes detected.");
            return;
        }

        const totalOps = increaseActions.length + reductionActions.length;
        setPhase("progress");
        dispatch(setTimeTableLoading(true));
        setProgress({done: 0, total: totalOps, message: "Starting..."});
        let done = 0;
        const failures: string[] = [];
        try {
            for (const action of increaseActions) {
                setProgress({done, total: totalOps, message: `${action.jiraId} ${action.date}`});
                try {
                    await saveWorklogEntry({jiraId: action.jiraId, category: DEFAULT_CATEGORY, date: action.date, hours: action.hours, comment: action.comment, impersonate});
                } catch (err: any) {
                    console.error("WeekActions: edit increase failed", action.jiraId, action.date, err);
                    failures.push(`${action.jiraId} on ${action.date} \u2014 ${err?.response?.data ? String(err.response.data) : String(err)}`);
                }
                done += 1;
                setProgress({done, total: totalOps, message: `${action.jiraId} ${action.date}`});
            }
            for (const action of reductionActions) {
                setProgress({done, total: totalOps, message: `booking ${action.bookingId}`});
                try {
                    await updateBookingHours({bookingId: action.bookingId, status: action.status, impersonate, newValue: action.newValue});
                } catch (err: any) {
                    console.error("WeekActions: edit reduction failed", action.bookingId, err);
                    failures.push(`booking ${action.bookingId} \u2014 ${err?.response?.data ? String(err.response.data) : String(err)}`);
                }
                done += 1;
                setProgress({done, total: totalOps, message: `booking ${action.bookingId}`});
            }
        } finally {
            dispatch(timetableRefresh());
            dispatch(jiraBoardrefreshNofilters());
            dispatch(setTimeTableLoading(false));
            setPhase("loading");
            onClose();
        }
        if (failures.length > 0) {
            const lines = failures.slice(0, 12).map((f) => `\u2022 ${f}`);
            showSwal("failed", `${failures.length} of ${totalOps} change(s) failed and were skipped (the rest were applied):\n\n${lines.join("\n")}`);
        }
    }

    if (phase === "progress") {
        return <ProgressOverlay isOpen title="Applying changes..." done={progress.done} total={progress.total} message={progress.message}/>;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Edit Selected Week</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    {phase === "loading" ? (
                        <Spinner/>
                    ) : (
                        <>
                            <InfoBar>
                                <Text>Week: {targetAnchor} to {targetDates[6] || ""}</Text>
                                <Text>Category: {DEFAULT_CATEGORY} | Overtime: false</Text>
                            </InfoBar>
                            {validationError && (
                                <Alert status="error" mb={3}><AlertIcon/>{validationError}</Alert>
                            )}
                            <EditableWeekTable
                                rows={rows}
                                setRows={setRows}
                                mode="edit"
                                jiraBaseUrl={jiraBaseUrl}
                                favorites={favorites}
                                onManageFavorites={() => navigate("/preferences")}
                            />
                            <Text mt={2} fontSize="xs" color="text-muted">Gray cells are approved and cannot be changed.</Text>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleApply} isDisabled={phase !== "review"}>Apply</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
